import { Component, Input, Output, EventEmitter } from '@angular/core';
import {
  HttpClient,
  HttpClientModule,
  HttpHeaders,
} from '@angular/common/http';
@Component({
  selector: 'app-customs-suggestion',
  standalone: true,
  imports: [HttpClientModule],
  template: ``,
  styles: [],
})
export class CustomsSuggestionComponent {
  /** Données fraîches du formulaire (utilise parentForm.value côté parent) */
  @Input() orderData: any;
  @Input() exportReasonEnum: Array<{ name: string; code: number }> = [];
  @Input() currencyEnum: Array<{ name: string; code: number }> = [];
  @Input() isFrench = false;
  @Output() customsSuggestions = new EventEmitter<any>();
  // ⚠️ Utilise un proxy backend sécurisé. Ne jamais exposer la clé côté client.
  private endpoint = 'https://ophtagestai.openai.azure.com/';
  private deploymentName = 'gpt-4';
  constructor(private http: HttpClient) {}
  generateSuggestions(KeyPart: string) {
    if (!this.orderData) return;
    const apiKey = '1Qgyai2EoTvfjkOrI3t5l1dbGDO3PfJrYzpyJ82' + KeyPart;
    const ER = (k: string, fallback: number) =>
      this.exportReasonEnum?.find?.((e) => (e?.name || '').endsWith('.' + k))
        ?.code ?? fallback;
    const exportCodes = {
      none: ER('none', 0),
      permanentSold: ER('permanentSold', 1),
      temporary: ER('temporary', 2),
      gift: ER('gift', 3),
      repairWarranty: ER('repairWarranty', 4),
      commercialSample: ER('commercialSample', 5),
      personalEffects: ER('personalEffects', 6),
      returnShipment: ER('returnShipment', 7),
      other: ER('other', 8),
    };
    // Fallback si le parent n'a rien passé (évite un prompt vide)
    const fallbackExport = [
      { name: 'none', code: 0 },
      { name: 'permanentSold', code: 1 },
      { name: 'temporary', code: 2 },
      { name: 'gift', code: 3 },
      { name: 'repairWarranty', code: 4 },
      { name: 'commercialSample', code: 5 },
      { name: 'personalEffects', code: 6 },
      { name: 'returnShipment', code: 7 },
      { name: 'other', code: 8 },
    ];
    const fallbackCurrency = [
      { name: 'NONE', code: 0 },
      { name: 'USD', code: 1 },
      { name: 'CAD', code: 2 },
      { name: 'KWD', code: 3 },
      { name: 'BHD', code: 4 },
      { name: 'OMR', code: 5 },
      { name: 'JOD', code: 6 },
      { name: 'GBP', code: 7 },
      { name: 'KYD', code: 8 },
      { name: 'EUR', code: 9 },
      { name: 'CHF', code: 10 },
    ];
    const exportEnum = this.exportReasonEnum?.length
      ? this.exportReasonEnum
      : fallbackExport;
    const currencyEnum = this.currencyEnum?.length
      ? this.currencyEnum
      : fallbackCurrency;
    const url = `${this.endpoint}openai/deployments/${this.deploymentName}/chat/completions?api-version=2025-01-01-preview`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'api-key': apiKey,
    });
    const normalizedDescription: string = (this.orderData?.productForm?.[0]?.description ?? '').toString().trim();
    const srcCountry = (
      this.orderData?.originCountry ||
      this.orderData?.OriginCountry ||
      ''
    ).toString();
    const dstCountry = (
      this.orderData?.destinationCountry ||
      this.orderData?.DestinationCountry ||
      ''
    ).toString();
    const carrier = (
      this.orderData?.carrier ||
      this.orderData?.Carrier ||
      ''
    ).toString();
    const language = this.isFrench ? 'French' : 'English';
    const body = {
      response_format: { type: 'json_object' },
      messages: [
        {
          role: 'system',
          content: `
          You are a customs AI assistant. Produce ONE valid JSON object for the Dutiable section.
          Respond with ONLY the JSON object, no markdown, no code fences, no prose.
          Language rule:
          - Current user language: ${language}
          - ItemDescription may be in English or French. Interpret French product descriptions normally for HS classification.
          - Localize ONLY textual fields (ExportReasonDetail, OfficialDescription, Broker). Keep numeric/code fields unchanged.
          - Use these localized examples as guidance:
            • English: "Documents only", "Sale of goods from X to Y", "Commercial sample", "Return shipment"
            • French: "Documents seulement", "Vente de marchandises de X vers Y", "Échantillon commercial", "Envoi de retour"
          HARD RULES:
          - Do NOT change the product category implied by ItemDescription.
          - First, infer the most likely HS chapter (2 digits) -> HSChapterGuess + HSChapterTitle.
          - Only propose HSCode values whose first two digits match HSChapterGuess. If confidence < 0.6 or ambiguity, set HSCode = "" and add ClarificationQuestions explaining what is missing.
          - Use dotted HS notation (e.g., "0302.13", "0302.13.90"). Use official phrasing for OfficialDescription per destination authority when HSCode is provided; if HSCode = "", OfficialDescription may be "".
          - If Broker/TaxID cannot be confidently inferred, set them to empty string (""). Do not invent brand-like brokers.
          ExportReason & Currency:
          - Use the enums provided below (exact codes).
          - Never leave ExportReason or ExportReasonDetail blank. If uncertain, apply the derivation rules below and ALWAYS return a short, non-empty explanation.
          Derivation rules:
          - If optionForm.IsDocumentOnly == true → ExportReason = code for "none"; ExportReasonDetail = localized "Documents only".
          - Else if shipper.countryID != consignee.countryID and productForm has at least one row with qty>0 → ExportReason = code for "permanentSold"; ExportReasonDetail = localized "Sale of goods from <OriginCountryCode> to <DestinationCountryCode>".
          - Else if the text contains "sample" → ExportReason = code for "commercialSample"; ExportReasonDetail = localized "Commercial sample".
          - Else if a return flag is present → ExportReason = code for "returnShipment"; ExportReasonDetail = localized "Return shipment".
          - Otherwise default to "permanentSold" with a localized brief explanation.
          Completeness rules:
          - Always include ALL fields shown in the target JSON shape.
          - Fields allowed to be empty string (""): Broker, TaxID, HSCode, OfficialDescription.
          - All other string fields MUST be non-empty.
          - ClarificationQuestions MUST be present; use [] if none.
          Use this enum for ExportReason (name+code, use the CODE numbers):
          ${JSON.stringify(exportEnum)}
          Use this enum for Currency (name+code, use the CODE numbers):
          ${JSON.stringify(currencyEnum)}
          Return exactly this JSON shape:
          {
            "ExportReason": number,
            "ExportReasonDetail": string,
            "Currency": number,
            "Broker": string,
            "TaxID": string,
            "OriginCountryCode": string,
            "DutiesAndTaxesPaidBy": number,
            "ClarificationQuestions": string[],
            "ShipmentDutiableRows": [
              {
                "ItemDescription": string,
                "Quantity": number,
                "DeclaredUnitValue": number,
                "CountryOfOrigin": string,
                "Weight": number,
                "HSChapterGuess": string,
                "HSChapterTitle": string,
                "Confidence": number,
                "HSCode": string,
                "OfficialDescription": string
              }
            ]
          }
          Validation:
          - If HSCode is provided and ItemDescription includes "frozen"/"deep-frozen" (or localized equivalents), HSCode MUST start with "0303" (unless fillet/meat → start with "0304").
          - If HSCode is provided and ItemDescription includes "fresh"/"chilled" (or localized equivalents), HSCode MUST start with "0302" (unless fillet/meat → start with "0304").
          - If these constraints cannot be satisfied, set HSCode = "" and add a ClarificationQuestions entry explaining why.
          Output constraint:
          - Output ONLY the JSON object; no leading/trailing text.
          `,
        },
        {
          role: 'user',
          content: `
          Order context:
          - OriginCountry: ${srcCountry || '(unknown)'}
          - DestinationCountry: ${dstCountry || '(unknown)'}
          - Carrier: ${carrier || '(unknown)'}
          - ItemDescription: ${normalizedDescription || '(empty)'}
          Data object (raw): ${JSON.stringify(this.orderData)}`,
        },
      ],
      temperature: 0,
      top_p: 1,
    };
    this.http.post(url, body, { headers }).subscribe({
      next: (response: any) => {
        const aiReply = response?.choices?.[0]?.message?.content ?? '';
        try {
          const suggestions = JSON.parse(aiReply);
          // --- Validations ---
          // HS formats: accept "03.02.13", "0302.13", "0302.13.90", "0302.13.90.10"
          const hsPattern =
            /^(?:\d{4}(?:\.\d{2}){0,3}|\d{6}(?:\.\d{2}){0,2}|\d{2}(?:\.\d{2}){1,4})$/;
          const rows: any[] = Array.isArray(suggestions?.ShipmentDutiableRows)
            ? suggestions.ShipmentDutiableRows
            : [];
          const chapterConsistencyGuard = rows.every((r: any) => {
            if (!r) return false;
            const chapterGuess = (r.HSChapterGuess ?? '').toString().trim();
            const confidence = Number(r.Confidence ?? 0);
            const hs = (r.HSCode ?? '').toString().trim();
            // HS empty acceptable si confiance faible ou si questions de clarification
            if (!hs) {
              const hasQuestions =
                Array.isArray(suggestions?.ClarificationQuestions) &&
                suggestions.ClarificationQuestions.length > 0;
              return confidence < 0.6 || hasQuestions;
            }
            if (!hsPattern.test(hs)) return false;
            const hsChapter = hs.replace(/\D/g, '').slice(0, 2); // robust: extrait 2 premiers digits
            return (
              chapterGuess && hsChapter === chapterGuess && confidence >= 0.6
            );
          });
          // ✅ Valider les codes via les enums passées par le parent (et pas via des ranges figés)
          const exportCodes = new Set(exportEnum.map((e) => e.code));
          const currencyCodes = new Set(currencyEnum.map((e) => e.code));
          const enumsOk =
            exportCodes.has(Number(suggestions?.ExportReason)) &&
            currencyCodes.has(Number(suggestions?.Currency));
          if (!chapterConsistencyGuard || !enumsOk) {
            console.error('Validation failed. Raw AI reply:', aiReply);
            this.customsSuggestions.emit(null);
            return;
          }
          this.customsSuggestions.emit(suggestions);
        } catch (e) {
          console.error('Failed to parse AI response:', aiReply);
          this.customsSuggestions.emit(null);
        }
      },
      error: (err) => {
        console.error('AI suggestion error:', err);
        this.customsSuggestions.emit(null);
      },
    });
  }
}
